<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const CATEGORIES = [
  { value: '', label: 'Todos' },
  { value: 'upcoming', label: 'Proximos do retorno' },
  { value: 'overdue', label: 'Atrasados' },
  { value: 'recovering', label: 'Em recuperacao' },
  { value: 'scheduled', label: 'Agendados' },
  { value: 'recovered', label: 'Recuperados' },
];

const clients = ref([]);
const totals = ref({});
const services = ref([]);
const establishments = ref([]);
const professionals = ref([]);
const expanded = ref(new Set());
const messageDrafts = reactive({});
const isLoading = ref(true);
const errorMessage = ref('');
const successMessage = ref('');
const assistedCallAvailable = ref(false);
const filters = reactive({
  category: '',
  establishmentId: '',
  serviceId: '',
  professionalId: '',
  minimumDaysOverdue: '0',
});
const router = useRouter();
const { accountId } = useAccount();

const hasClients = computed(() => clients.value.length > 0);

function formatDate(value) {
  if (!value) return 'Sem data';
  return new Intl.DateTimeFormat('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function categoryLabel(value) {
  return CATEGORIES.find(item => item.value === value)?.label || value;
}

function urgencyClass(value) {
  const classes = {
    high: 'bg-n-ruby-3 text-n-ruby-11',
    medium: 'bg-n-amber-3 text-n-amber-11',
    low: 'bg-n-teal-3 text-n-teal-11',
  };
  return classes[value] || classes.low;
}

function urgencyLabel(value) {
  return { high: 'Prioritario', medium: 'Acompanhar', low: 'Em dia' }[value] || value;
}

function toggleClient(clientId) {
  const next = new Set(expanded.value);
  next.has(clientId) ? next.delete(clientId) : next.add(clientId);
  expanded.value = next;
}

function openContact(client) {
  if (!client.contactId) return;
  router.push(`/app/accounts/${accountId.value}/contacts/${client.contactId}?page=1`);
}

function callClient(client) {
  if (!client.phone) return;
  window.location.href = `tel:${client.phone}`;
}

async function fetchLookups() {
  try {
    const [servicesResponse, establishmentsResponse, professionalsResponse] =
      await Promise.all([
        fetch('/api/superwoot/services'),
        fetch('/api/superwoot/establishments'),
        fetch('/api/superwoot/professionals'),
      ]);
    if (!servicesResponse.ok || !establishmentsResponse.ok || !professionalsResponse.ok) {
      throw new Error('lookup_failed');
    }
    const [servicesPayload, establishmentsPayload, professionalsPayload] =
      await Promise.all([
        servicesResponse.json(),
        establishmentsResponse.json(),
        professionalsResponse.json(),
      ]);
    services.value = servicesPayload.services || [];
    establishments.value = establishmentsPayload.establishments || [];
    professionals.value = professionalsPayload.professionals || [];
  } catch {
    errorMessage.value = 'Nao foi possivel carregar os filtros.';
  }
}

async function fetchRetention() {
  isLoading.value = true;
  errorMessage.value = '';
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && !(key === 'minimumDaysOverdue' && value === '0')) {
      params.set(key, value);
    }
  });
  try {
    const query = params.toString();
    const response = await fetch(`/api/superwoot/retention${query ? `?${query}` : ''}`);
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'request_failed');
    clients.value = payload.clients || [];
    totals.value = payload.totals || {};
    assistedCallAvailable.value = payload.assistedCallAvailable === true;
  } catch {
    clients.value = [];
    errorMessage.value = 'Nao foi possivel carregar a operacao de retencao.';
  } finally {
    isLoading.value = false;
  }
}

async function runAction(profileId, action, extra = {}) {
  errorMessage.value = '';
  successMessage.value = '';
  try {
    const response = await fetch('/api/superwoot/retention', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profileId, action, ...extra }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'action_failed');
    successMessage.value =
      action === 'pause'
        ? 'Recuperacao pausada.'
        : action === 'resume'
          ? 'Recuperacao retomada.'
          : 'Mensagem enviada pelo Chatwoot.';
    await fetchRetention();
  } catch {
    errorMessage.value = 'Nao foi possivel executar esta acao.';
  }
}

async function sendMessage(cycle) {
  const message = messageDrafts[cycle.profileId]?.trim();
  if (!message) {
    errorMessage.value = 'Escreva a mensagem antes de enviar.';
    return;
  }
  await runAction(cycle.profileId, 'send_message', { message });
  messageDrafts[cycle.profileId] = '';
}

watch(filters, fetchRetention, { deep: true });
onMounted(async () => {
  await Promise.all([fetchLookups(), fetchRetention()]);
});
</script>

<template>
  <section class="flex flex-col flex-1 min-w-0 h-full bg-n-background">
    <header class="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-n-weak bg-n-solid-2">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">Retencao</h1>
        <p class="mt-1 text-sm text-n-slate-11">
          Retornos esperados e recuperacao de clientes recorrentes
        </p>
      </div>
      <Button icon="i-lucide-refresh-cw" color="slate" size="sm" title="Atualizar retencao" @click="fetchRetention" />
    </header>

    <div class="flex flex-wrap gap-2 px-6 py-3 border-b border-n-weak bg-n-solid-1">
      <select v-model="filters.category" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12">
        <option v-for="item in CATEGORIES" :key="item.value" :value="item.value">{{ item.label }}</option>
      </select>
      <select v-model="filters.establishmentId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12">
        <option value="">Todas as unidades</option>
        <option v-for="item in establishments" :key="item.id" :value="item.id">{{ item.name }}</option>
      </select>
      <select v-model="filters.serviceId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12">
        <option value="">Todos os servicos</option>
        <option v-for="item in services" :key="item.id" :value="item.id">{{ item.name }}</option>
      </select>
      <select v-model="filters.professionalId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12">
        <option value="">Todos os profissionais</option>
        <option v-for="item in professionals" :key="item.id" :value="item.id">{{ item.name }}</option>
      </select>
      <label class="flex items-center gap-2 text-xs text-n-slate-11">
        Atraso minimo
        <input v-model="filters.minimumDaysOverdue" type="number" min="0" max="730" class="w-16 h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12" />
      </label>
    </div>

    <div class="grid grid-cols-2 gap-px border-b md:grid-cols-6 border-n-weak bg-n-weak">
      <button v-for="item in CATEGORIES.slice(1)" :key="item.value" type="button" class="px-4 py-3 text-left bg-n-solid-2 hover:bg-n-alpha-2" @click="filters.category = item.value">
        <span class="block text-xs text-n-slate-10">{{ item.label }}</span>
        <strong class="block mt-1 text-lg font-medium text-n-slate-12">{{ totals[item.value] || 0 }}</strong>
      </button>
      <div class="px-4 py-3 bg-n-solid-2">
        <span class="block text-xs text-n-slate-10">Clientes visiveis</span>
        <strong class="block mt-1 text-lg font-medium text-n-slate-12">{{ totals.clients || 0 }}</strong>
      </div>
    </div>

    <p v-if="errorMessage" class="px-6 pt-4 text-sm text-n-ruby-11">{{ errorMessage }}</p>
    <p v-if="successMessage" class="px-6 pt-4 text-sm text-n-teal-11">{{ successMessage }}</p>

    <div class="flex-1 min-h-0 p-6 overflow-auto">
      <p v-if="isLoading" class="text-sm text-n-slate-10">Atualizando retencao...</p>
      <div v-else-if="!hasClients" class="max-w-xl p-4 border rounded-md border-n-weak bg-n-solid-2">
        <h2 class="text-sm font-medium text-n-slate-12">Nenhum cliente recorrente encontrado</h2>
        <p class="mt-2 text-sm leading-6 text-n-slate-10">
          Os clientes aparecem aqui depois que um atendimento concluido gera um ciclo de retorno para um servico com recorrencia ativa.
        </p>
      </div>

      <div v-else class="flex flex-col gap-3">
        <article v-for="client in clients" :key="client.id" class="overflow-hidden border rounded-md border-n-weak bg-n-solid-2">
          <div class="flex flex-wrap items-center gap-3 p-4">
            <button type="button" class="text-left grow" @click="toggleClient(client.id)">
              <strong class="block text-sm font-medium text-n-slate-12">{{ client.name }}</strong>
              <span class="block mt-1 text-xs text-n-slate-10">{{ client.phone || client.email || 'Contato sem telefone' }}</span>
            </button>
            <span class="px-2 py-1 text-xs rounded" :class="urgencyClass(client.urgency)">{{ urgencyLabel(client.urgency) }}</span>
            <span class="text-xs text-n-slate-10">{{ client.cycles.length }} ciclo(s)</span>
            <Button icon="i-lucide-contact" size="sm" slate title="Abrir contato" :disabled="!client.contactId" @click="openContact(client)" />
            <Button icon="i-lucide-phone" size="sm" slate title="Ligacao humana assistida" :disabled="!client.phone" @click="callClient(client)" />
            <Button :icon="expanded.has(client.id) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" size="sm" slate title="Ver ciclos" @click="toggleClient(client.id)" />
          </div>

          <div v-if="expanded.has(client.id)" class="border-t border-n-weak">
            <section v-for="cycle in client.cycles" :key="cycle.profileId" class="p-4 border-b last:border-b-0 border-n-weak">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <strong class="block text-sm font-medium text-n-slate-12">{{ cycle.service.name }}</strong>
                  <span class="block mt-1 text-xs text-n-slate-10">{{ cycle.establishment?.name || 'Sem unidade' }}<template v-if="cycle.professional?.name"> · {{ cycle.professional.name }}</template></span>
                  <span class="block mt-1 text-xs text-n-slate-10">Ultimo atendimento: {{ formatDate(cycle.lastVisitAt) }} · retorno: {{ formatDate(cycle.nextDueAt) }}</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <span class="px-2 py-1 text-xs rounded bg-n-slate-3 text-n-slate-11">{{ categoryLabel(cycle.category) }}</span>
                  <span v-if="cycle.daysOverdue" class="px-2 py-1 text-xs rounded bg-n-ruby-3 text-n-ruby-11">{{ cycle.daysOverdue }} dia(s) de atraso</span>
                </div>
              </div>

              <p v-if="cycle.latestAttempt" class="mt-3 text-xs text-n-slate-10">
                Ultima tentativa: {{ cycle.latestAttempt.stage }} · {{ cycle.latestAttempt.status }} · {{ formatDate(cycle.latestAttempt.sent_at || cycle.latestAttempt.scheduled_for) }}
              </p>
              <p v-if="cycle.blockedReason" class="mt-2 text-xs text-n-amber-11">Bloqueio: {{ cycle.blockedReason }}</p>

              <div class="flex flex-wrap gap-2 mt-3">
                <Button v-if="cycle.status !== 'paused'" label="Pausar" icon="i-lucide-pause" size="sm" slate @click="runAction(cycle.profileId, 'pause')" />
                <Button v-else label="Retomar" icon="i-lucide-play" size="sm" slate @click="runAction(cycle.profileId, 'resume')" />
              </div>

              <form class="flex flex-col gap-2 mt-3 md:flex-row" @submit.prevent="sendMessage(cycle)">
                <input v-model="messageDrafts[cycle.profileId]" type="text" placeholder="Mensagem manual pelo Chatwoot" class="h-8 px-2 text-sm border rounded-md grow border-n-weak bg-n-solid-1 text-n-slate-12" />
                <Button label="Enviar" icon="i-lucide-send" size="sm" type="submit" :disabled="!client.conversationId" />
              </form>
            </section>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
